import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import SalesReportForm from './SalesReportForm';
import './SalesReportsDashboard.css';

function SalesReportsDashboard() {
  const { user, hasPermission } = useAuth();
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [filters, setFilters] = useState({
    employee: 'all',
    status: 'all',
    startDate: '',
    endDate: ''
  });

  // Check permissions
  const canViewAll = hasPermission('sales-reports', 'read') || user?.role?.name === 'HR' || user?.role?.name === 'Admin';
  const canCreate = user?.role?.name === 'Sales' || hasPermission('sales-reports', 'create');
  const canReview = user?.role?.name === 'HR' || user?.role?.name === 'Admin';

  useEffect(() => {
    fetchReports();
    if (canViewAll) {
      fetchAnalytics();
      fetchEmployees();
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'all') {
          params.append(key, filters[key]);
        }
      });

      const response = await api.get(`/sales-reports?${params.toString()}`);
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching sales reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.employee !== 'all') params.append('employee', filters.employee);

      const response = await api.get(`/sales-reports/analytics/dashboard?${params.toString()}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      const salesEmployees = response.data.filter(emp => 
        emp.role?.name === 'Sales' || emp.role?.permissions?.some(p => p.module === 'leads')
      );
      setEmployees(salesEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreateReport = () => {
    setEditingReport(null);
    setShowForm(true);
  };

  const handleEditReport = (report) => {
    setEditingReport(report);
    setShowForm(true);
  };

  const handleSaveReport = (savedReport) => {
    if (editingReport) {
      setReports(prev => prev.map(r => r._id === savedReport._id ? savedReport : r));
    } else {
      setReports(prev => [savedReport, ...prev]);
    }
    setShowForm(false);
    setEditingReport(null);
    if (canViewAll) fetchAnalytics();
  };

  const handleReviewReport = async (reportId, reviewComments) => {
    try {
      const response = await api.put(`/sales-reports/${reportId}/review`, { reviewComments });
      setReports(prev => prev.map(r => r._id === reportId ? response.data : r));
    } catch (error) {
      console.error('Error reviewing report:', error);
      alert('Error reviewing report');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: '#6c757d', text: 'Draft' },
      submitted: { color: '#007bff', text: 'Submitted' },
      reviewed: { color: '#28a745', text: 'Reviewed' }
    };
    const config = statusConfig[status] || statusConfig.submitted;
    return (
      <span 
        className="status-badge"
        style={{ backgroundColor: config.color }}
      >
        {config.text}
      </span>
    );
  };

  if (showForm) {
    return (
      <SalesReportForm
        report={editingReport}
        onSave={handleSaveReport}
        onCancel={() => {
          setShowForm(false);
          setEditingReport(null);
        }}
      />
    );
  }

  return (
    <div className="sales-reports-dashboard">
      <div className="dashboard-header">
        <h2>📊 Daily Work Reports - Sales Team</h2>
        <p>Track and monitor sales team performance</p>
        {canCreate && (
          <button className="btn-create-report" onClick={handleCreateReport}>
            + Create Report
          </button>
        )}
      </div>

      {/* Analytics Section - Only for HR/Admin */}
      {canViewAll && analytics && (
        <div className="analytics-section">
          <h3>📈 Sales Performance Summary</h3>
          <div className="analytics-grid">
            <div className="analytics-card">
              <div className="card-icon">📞</div>
              <div className="card-content">
                <h4>Total Outbound Calls Made</h4>
                <p className="metric-value">{analytics.totalCalls || 0}</p>
                <small>Connection Rate: {analytics.connectionRate || 0}%</small>
              </div>
            </div>

            <div className="analytics-card">
              <div className="card-icon">✅</div>
              <div className="card-content">
                <h4>Successful Call Connections</h4>
                <p className="metric-value">{analytics.totalConnections || 0}</p>
                <small>Avg duration: {Math.round(analytics.totalCallDuration / analytics.totalReports) || 0} min</small>
              </div>
            </div>

            <div className="analytics-card">
              <div className="card-icon">🔄</div>
              <div className="card-content">
                <h4>Follow-up Interactions Completed</h4>
                <p className="metric-value">{analytics.totalFollowUps || 0}</p>
                <small>From {analytics.totalReports || 0} reports</small>
              </div>
            </div>

            <div className="analytics-card">
              <div className="card-icon">🎯</div>
              <div className="card-content">
                <h4>Qualified Interested Prospects</h4>
                <p className="metric-value">{analytics.totalQualifiedProspects || 0}</p>
                <small>Conversion Rate: {analytics.conversionRate || 0}%</small>
              </div>
            </div>

            <div className="analytics-card">
              <div className="card-icon">💼</div>
              <div className="card-content">
                <h4>Sales Pipeline Opportunities Generated</h4>
                <p className="metric-value">{analytics.totalPipelineOpportunities || 0}</p>
                <small>Total deals value: {formatCurrency(analytics.totalDealsValue)}</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Section */}
      {canViewAll && (
        <div className="filters-section">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Sales Person:</label>
              <select 
                value={filters.employee} 
                onChange={(e) => handleFilterChange('employee', e.target.value)}
              >
                <option value="all">All Sales People</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {emp.firstName} {emp.lastName} ({emp.employeeId})
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Status:</label>
              <select 
                value={filters.status} 
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="submitted">Submitted</option>
                <option value="reviewed">Reviewed</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Start Date:</label>
              <input 
                type="date" 
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>End Date:</label>
              <input 
                type="date" 
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
          </div>

          <button 
            className="clear-filters-btn"
            onClick={() => setFilters({
              employee: 'all',
              status: 'all',
              startDate: '',
              endDate: ''
            })}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Reports List */}
      <div className="reports-section">
        <h3>📋 {canViewAll ? 'All Reports' : 'My Reports'}</h3>
        
        {loading ? (
          <div className="loading">Loading reports...</div>
        ) : reports.length === 0 ? (
          <div className="no-data">
            <p>No reports found matching the selected criteria.</p>
            {canCreate && (
              <button className="btn-create-report" onClick={handleCreateReport}>
                Create Your First Report
              </button>
            )}
          </div>
        ) : (
          <div className="reports-grid">
            {reports.map(report => (
              <div key={report._id} className="report-card">
                <div className="report-header">
                  <div className="report-info">
                    <h4>{formatDate(report.reportDate)}</h4>
                    {canViewAll && (
                      <p className="employee-name">
                        {report.employee.firstName} {report.employee.lastName} 
                        <span className="employee-id">({report.employee.employeeId})</span>
                      </p>
                    )}
                  </div>
                  {getStatusBadge(report.status)}
                </div>

                <div className="report-metrics">
                  <div className="metric-row">
                    <span className="metric-label">📞 Calls:</span>
                    <span className="metric-value">{report.totalOutboundCalls}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">✅ Connections:</span>
                    <span className="metric-value">{report.successfulConnections}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">🎯 Qualified:</span>
                    <span className="metric-value">{report.qualifiedProspects}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">💼 Pipeline:</span>
                    <span className="metric-value">{report.pipelineOpportunities}</span>
                  </div>
                  {report.dealsClosedValue > 0 && (
                    <div className="metric-row">
                      <span className="metric-label">💰 Deals:</span>
                      <span className="metric-value">{formatCurrency(report.dealsClosedValue)}</span>
                    </div>
                  )}
                </div>

                {report.achievements && (
                  <div className="report-notes">
                    <strong>Achievements:</strong>
                    <p>{report.achievements}</p>
                  </div>
                )}

                <div className="report-actions">
                  {(report.employee._id === user?._id || user?.role?.name === 'Admin') && (
                    <button 
                      className="btn-edit"
                      onClick={() => handleEditReport(report)}
                    >
                      Edit
                    </button>
                  )}
                  
                  {canReview && report.status === 'submitted' && (
                    <button 
                      className="btn-review"
                      onClick={() => {
                        const comments = prompt('Review comments (optional):');
                        if (comments !== null) {
                          handleReviewReport(report._id, comments);
                        }
                      }}
                    >
                      Mark Reviewed
                    </button>
                  )}
                </div>

                {report.status === 'reviewed' && report.reviewComments && (
                  <div className="review-section">
                    <small>
                      <strong>Review by {report.reviewedBy?.firstName} {report.reviewedBy?.lastName}:</strong>
                      <br/>{report.reviewComments}
                    </small>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SalesReportsDashboard;